import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { FormField } from '@shared/components/add-form/add-form.component';
import { PosShiftOpenService } from './services/pos-shift-open.service';

// ============================================================================
// INTERFACES
// ============================================================================

export interface PosShiftOpenFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: any[];
  listRoute: string;
}

// ============================================================================
// FORM FIELD OPTIONS
// ============================================================================

export const SHIFT_STATUS_OPTIONS = [
  { label: 'pos.shift_open.status.open', value: 1 },
  { label: 'pos.shift_open.status.create', value: 0 },
];

// ============================================================================
// SEARCH FORM CONFIGURATIONS
// ============================================================================

// Search configuration with dynamic options
export function getPosShiftOpenSearchConfig(
  posShiftOpenService: PosShiftOpenService
): FormFieldConfig[] {
  return [
    {
      key: 'name',
      label: 'pos.shift_open.search.fields.cashier_name',
      type: 'text',
      placeholder: 'pos.shift_open.search.placeholders.enter_cashier_name',
    },
    {
      key: 'posId',
      label: 'pos.shift_open.search.fields.pos_code',
      type: 'select',
      placeholder: 'pos.shift_open.search.placeholders.select_pos_code',
      options: [], // Will be populated dynamically by the component
    },
    // {
    //   key: 'startTimeFrom',
    //   label: 'pos.shift_open.search.fields.start_time_from',
    //   type: 'dateRange',
    //   range: { startKey: 'startTimeFrom', endKey: 'startTimeTo' },
    // },
    // {
    //   key: 'status',
    //   label: 'pos.shift_open.search.fields.status',
    //   type: 'select',
    //   options: SHIFT_STATUS_OPTIONS,
    //   placeholder: 'pos.shift_open.search.placeholders.select_status',
    // }
  ];
}

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names (shown on first load)
 * Update these constants to change column property names
 */
export const POS_SHIFT_OPEN_DEFAULT_COLUMNS = {
  SHIFT_ID: 'shift_id',
  POS_CODE: 'posCode',
  SHOP_NAME: 'shopName',
  CASHIER_NAME: 'cashierName',
  OPENING_TIME: 'openingTime',
  OPENING_AMOUNT: 'openingAmount',
} as const;

/**
 * Flexible column property names (can be shown/hidden via column selector)
 * Update these constants to change flexible column property names
 */
export const POS_SHIFT_OPEN_FLEXIBLE_COLUMNS = {
  SHIFT_DATE: 'shiftDate',
  STATUS: 'status',
} as const;

/**
 * Action column property name
 */
export const POS_SHIFT_OPEN_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order (shown on first load)
 * Update this array to change the default column order
 */
export const POS_SHIFT_OPEN_DEFAULT_COLUMNS_ORDER: string[] = [
  POS_SHIFT_OPEN_DEFAULT_COLUMNS.SHIFT_ID,
  POS_SHIFT_OPEN_DEFAULT_COLUMNS.POS_CODE,
  POS_SHIFT_OPEN_DEFAULT_COLUMNS.SHOP_NAME,
  POS_SHIFT_OPEN_DEFAULT_COLUMNS.CASHIER_NAME,
  POS_SHIFT_OPEN_DEFAULT_COLUMNS.OPENING_TIME,
  POS_SHIFT_OPEN_DEFAULT_COLUMNS.OPENING_AMOUNT,
];

/**
 * Flexible columns array (can be shown/hidden via column selector)
 * Update this array to change which columns are flexible
 */
export const POS_SHIFT_OPEN_FLEXIBLE_COLUMNS_ORDER: string[] = [
  POS_SHIFT_OPEN_FLEXIBLE_COLUMNS.SHIFT_DATE,
  POS_SHIFT_OPEN_FLEXIBLE_COLUMNS.STATUS,
];

export const POS_SHIFT_OPEN_COLUMNS: TableColumn[] = [
  {
    label: 'pos.shift_open.table.columns.shift_id',
    prop: POS_SHIFT_OPEN_DEFAULT_COLUMNS.SHIFT_ID,
    width: 150,
  },
  {
    label: 'pos.shift_open.table.columns.pos_code',
    prop: POS_SHIFT_OPEN_DEFAULT_COLUMNS.POS_CODE,
    width: 120,
  },
  {
    label: 'pos.shift_open.table.columns.shop_name',
    prop: POS_SHIFT_OPEN_DEFAULT_COLUMNS.SHOP_NAME,
    width: 160,
  },
  {
    label: 'pos.shift_open.table.columns.cashier_name',
    prop: POS_SHIFT_OPEN_DEFAULT_COLUMNS.CASHIER_NAME,
    width: 140,
  },
  {
    label: 'pos.shift_open.table.columns.shift_date',
    prop: POS_SHIFT_OPEN_FLEXIBLE_COLUMNS.SHIFT_DATE,
    width: 120,
  },
  {
    label: 'pos.shift_open.table.columns.opening_time',
    prop: POS_SHIFT_OPEN_DEFAULT_COLUMNS.OPENING_TIME,
    width: 120,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'pos.shift_open.table.columns.opening_amount',
    prop: POS_SHIFT_OPEN_DEFAULT_COLUMNS.OPENING_AMOUNT,
    width: 130,
    type: 'custom',
    template: 'currencyTemplate',
  },
  {
    label: 'pos.shift_open.table.columns.status',
    prop: POS_SHIFT_OPEN_FLEXIBLE_COLUMNS.STATUS,
    width: 100,
    type: 'status',
  },
  {
    label: 'pos.shift_open.table.columns.actions',
    prop: POS_SHIFT_OPEN_ACTION_COLUMN.ACTIONS,
    width: 100,
    type: 'actions',
  },
];

// ============================================================================
// FORM CONFIGURATION FOR ADD-FORM COMPONENT
// ============================================================================

export function getPosShiftOpenFormConfig(
  posShiftOpenService: PosShiftOpenService
): PosShiftOpenFormConfig {
  return {
    title: 'pos.shift_open.form.view_shift_opening',
    listRoute: '/pos/shift-open',
    headerActions: [
      {
        label: 'common.actions.go_back',
        severity: 'secondary',
        outlined: true,
        action: 'back',
      },
    ],
    formFields: [
      {
        controlName: 'shift_id',
        label: 'pos.shift_open.form.fields.shift_id',
        type: 'text',
        readonly: true,
        section: 'pos.shift_open.form.sections.staff_info',
      },
      {
        controlName: 'cashierName',
        label: 'pos.shift_open.form.fields.cashier_name',
        type: 'text',
        readonly: true,
        section: 'pos.shift_open.form.sections.staff_info',
      },
      {
        controlName: 'posCode',
        label: 'pos.shift_open.form.fields.pos_code',
        type: 'text',
        readonly: true,
        section: 'pos.shift_open.form.sections.staff_info',
      },
      {
        controlName: 'shopName',
        label: 'pos.shift_open.form.fields.shop_name',
        type: 'text',
        readonly: true,
        section: 'pos.shift_open.form.sections.staff_info',
      },
      {
        controlName: 'shopAddress',
        label: 'pos.shift_open.form.fields.shop_address',
        type: 'text',
        readonly: true,
        section: 'pos.shift_open.form.sections.staff_info',
      },
      // {
      //   controlName: 'shiftDate',
      //   label: 'pos.shift_open.form.fields.shift_date',
      //   type: 'text',
      //   readonly: true,
      //   section: 'pos.shift_open.form.sections.shift_info'
      // },
      {
        controlName: 'openingTime',
        label: 'pos.shift_open.form.fields.opening_time',
        type: 'text',
        readonly: true,
        section: 'pos.shift_open.form.sections.shift_info',
      },
      {
        controlName: 'openingAmount',
        label: 'pos.shift_open.form.fields.opening_amount',
        type: 'currency',
        readonly: true,
        min: 0,
        section: 'pos.shift_open.form.sections.shift_info',
      },
    ],
  };
}
